/* =====================================================
   ui-security-reactive · PermissionService
   Permisos del usuario conectado, resueltos contra bizmetry-shared
   (GET /session/permissions) y cacheados por usuario. La UI habilita o
   deshabilita acciones con esto; el backend sigue validando cada operación.

   Usage:
     PermissionService.can('BROWSE_RESOURCES')            // síncrono, para templates/controllers
     PermissionService.can(['CREATE_PROFILE', 'IMPORT_PROFILE'])  // alcanza con uno
     PermissionService.canSecure(any, all)                // alguno de `any` y todos los de `all`
     PermissionService.has('BROWSE_RESOURCES').then(...)  // espera la carga
     PermissionService.refresh()                          // recarga ignorando el caché
     PermissionService.reset()                            // descarta el caché (ej. logout)

   Acepta KEYs de PERMISSIONS ('BROWSE_RESOURCES') o IDs ('permission-browse-resources').
   Cada carga exitosa emite 'permissions:changed' en $rootScope.

   Se refresca solo (ver el run block al final): al abrir cualquier $mdDialog,
   al cambiar de ruta y cuando la ventana recupera el foco. Como can() se evalúa
   en cada digest, los botones con bm-requires se actualizan sin código extra.
   ===================================================== */
angular.module('bizmetryApp')
  .service('PermissionService', [
    '$http', '$q', '$rootScope', 'CONFIG', 'PERMISSIONS', 'PERMISSION_LABELS',
    function ($http, $q, $rootScope, CONFIG, PERMISSIONS, PERMISSION_LABELS) {

      const PERMISSIONS_URL = CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-shared/session/permissions';
      const self = this;

      let cachedUserId = null;
      let permissionIds = null;   // Set de IDs, null mientras no se cargó
      let pending = null;

      function currentUserId() {
        try {
          const user = JSON.parse(sessionStorage.getItem('user') || '{}');
          return user && user.id ? user.id : null;
        } catch (e) {
          return null;
        }
      }

      function authConfig() {
        try {
          const tokenInfo = JSON.parse(sessionStorage.getItem('authTokenInfo') || 'null');
          if (tokenInfo && tokenInfo.access_token) {
            return { headers: { Authorization: 'Bearer ' + tokenInfo.access_token } };
          }
        } catch (e) { /* sin token: va el header default de $http */ }
        return {};
      }

      function toId(permission) {
        return PERMISSIONS[permission] || permission;
      }

      this.toId = toId;

      this.reset = function () {
        cachedUserId = null;
        permissionIds = null;
        pending = null;
      };

      function publish(ids) {
        permissionIds = ids;
        $rootScope.$broadcast('permissions:changed', ids);
        return ids;
      }

      // Pide los permisos. Mientras tanto el Set anterior (si lo hay) sigue
      // respondiendo can(), así los botones no parpadean durante el request.
      // 401/403 (ej. le quitaron GET_PERMISSIONS) → sin permisos; un error de
      // red conserva lo que había.
      function fetch(userId) {
        pending = $http.get(PERMISSIONS_URL, authConfig())
          .then(function (response) {
            const list = (response.data && response.data.permissions) || [];
            return publish(new Set(list.map(function (p) { return p.id; })));
          })
          .catch(function (error) {
            const status = error && error.status;
            console.warn('🔐 [PermissionService] Could not load permissions:', status);
            if (status === 401 || status === 403) return publish(new Set());
            return permissionIds || new Set();
          })
          .finally(function () { pending = null; });
        cachedUserId = userId;
        return pending;
      }

      function sameUser() {
        const userId = currentUserId();
        if (userId !== cachedUserId) self.reset();
        return userId;
      }

      // Resuelve con el Set de IDs; usa el caché si ya cargó.
      this.load = function () {
        const userId = sameUser();
        if (!userId) return $q.resolve(new Set());   // sin sesión (login, landing)
        if (permissionIds) return $q.resolve(permissionIds);
        return pending || fetch(userId);
      };

      // Recarga ignorando el caché (en segundo plano).
      this.refresh = function () {
        const userId = sameUser();
        if (!userId) return $q.resolve(new Set());
        return pending || fetch(userId);
      };

      this.has = function (permission, options) {
        const load = options && options.refresh ? self.refresh() : self.load();
        return load.then(function (ids) { return ids.has(toId(permission)); });
      };

      // Síncrono: false mientras no cargó (y dispara la carga la primera vez).
      // Con una lista, alcanza con tener uno.
      this.can = function (permissions) {
        if (!self.isLoaded()) {
          if (!pending) self.load();
          return false;
        }
        const list = Array.isArray(permissions) ? permissions : [permissions];
        return list.some(function (p) { return permissionIds.has(toId(p)); });
      };

      function asList(value) {
        if (!value) return [];
        return (Array.isArray(value) ? value : [value]).filter(Boolean);
      }

      // Para bm-requires: alguno de `any` (vacío = sin requisito) Y todos los de `all`.
      this.canSecure = function (any, all) {
        const anyList = asList(any), allList = asList(all);
        if (!anyList.length && !allList.length) return true;
        if (!self.isLoaded()) { self.can([]); return false; }   // dispara la carga
        return (!anyList.length || self.can(anyList)) &&
               allList.every(function (p) { return self.can(p); });
      };

      function label(p) {
        const id = toId(p);
        return PERMISSION_LABELS[id] || id;
      }

      // "Requires X or Y and Z permission" — solo lista lo que falta.
      this.requiresTooltip = function (any, all) {
        const anyList = asList(any), allList = asList(all);
        const parts = [];
        if (anyList.length && !self.can(anyList)) parts.push(anyList.map(label).join(' or '));
        allList.filter(function (p) { return !self.can(p); }).forEach(function (p) { parts.push(label(p)); });
        return parts.length ? 'Requires ' + parts.join(' and ') + ' permission' : '';
      };

      this.isLoaded = function () {
        return !!permissionIds && cachedUserId === currentUserId();
      };
    }
  ])

  // Refresco automático: así cualquier pantalla ve los permisos vigentes sin
  // pedirlos explícitamente.
  .run(['$rootScope', '$mdDialog', '$window', 'PermissionService',
    function ($rootScope, $mdDialog, $window, PermissionService) {
      const FOCUS_THROTTLE_MS = 10000;
      let lastFocusRefresh = 0;

      // Disponible en todos los templates (en scopes aislados: $root.can(...)).
      $rootScope.can = PermissionService.can;
      $rootScope.canSecure = PermissionService.canSecure;
      $rootScope.requiresTooltip = PermissionService.requiresTooltip;

      $rootScope.$on('$routeChangeSuccess', function () { PermissionService.refresh(); });

      const originalShow = $mdDialog.show;
      $mdDialog.show = function () {
        PermissionService.refresh();
        return originalShow.apply($mdDialog, arguments);
      };

      $window.addEventListener('focus', function () {
        const now = Date.now();
        if (now - lastFocusRefresh < FOCUS_THROTTLE_MS) return;
        lastFocusRefresh = now;
        PermissionService.refresh();
      });
    }
  ]);
